import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';
import { getDaybookItem, ResponseGetDaybookItem } from '../api/getDaybookItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import Quill from 'quill';
import { decrypt } from '@/utils/decrypt';
import { motion } from 'framer-motion';
import { HeaderInfo } from '@/UI/HeaderInfo';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { getDaybookVisualConfig } from '../components/DaybookTypeConfig';

import 'quill/dist/quill.bubble.css';

export const DaybookInner = () => {
  const quillRef = useRef<HTMLDivElement>(null);
  const { client, daybookId } = useParams();

  const { data, isFetching } = useQuery<ResponseGetDaybookItem>({
    queryKey: ['daybook', client, daybookId],
    staleTime: 5 * 60 * 1000,
    queryFn: () => getDaybookItem({ user: client!, daybookId: daybookId! }),
  });

  useEffect(() => {
    document.querySelector('main')?.scrollTo(0, 0);
  }, []);

  const [displayText, setDisplayText] = useState<string | null>(null);

  useEffect(() => {
    const handleDecryption = async () => {
      if (data?.textBin) {
        try {
          const decrypted = await decrypt(data.textBin, client!);
          setDisplayText(decrypted);
        } catch (error) {
          Sentry.captureException(error);
          setDisplayText(data.text ?? '');
        }
      } else if (data?.text) {
        setDisplayText(data.text ?? '');
      }
    };

    handleDecryption();
  }, [data?.text, data?.textBin, client]);

  useEffect(() => {
    if (quillRef.current && displayText) {
      quillRef.current.innerHTML = '';
      const quill = new Quill(quillRef.current, {
        theme: 'bubble',
        readOnly: true,
        modules: {
          clipboard: false,
          toolbar: false,
          history: false,
          scroll: false,
          keyboard: true,
          uploader: false,
        },
      });

      // Устанавливаем данные в редактор
      try {
        quill.setContents(JSON.parse(displayText));
      } catch {
        quill.setText(displayText);
      }
    }
  }, [displayText]);

  const config = useMemo(
    () => getDaybookVisualConfig(data?.typeDaybook),
    [data?.typeDaybook]
  );

  const formattedTime = useMemo(() => {
    if (!data?.creationDate) return null;
    const d = dayjs(data.creationDate);
    return d.isValid() ? d.format('HH:mm') : null;
  }, [data?.creationDate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <HeaderInfo
        title={
          isFetching
            ? ''
            : dayjs(data?.creationDate).format('D MMMM YYYY')
        }
        subtitle={formattedTime ? `Время записи: ${formattedTime}` : undefined}
        backUrl={`/${client}/daybook`}
        actionButton={
          data ? (
            <div
              className={clsx(
                'flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-md',
                config.badgeClass
              )}
            >
              {config.icon}
              <span>{config.title}</span>
            </div>
          ) : undefined
        }
      >
        <div className="mt-6 md:mt-8">
          <div
            className={clsx(
              'relative overflow-hidden rounded-2xl border p-6 md:p-8 backdrop-blur-md transition-all duration-300',
              config.cardBgClass,
              config.cardBorderClass
            )}
          >
            {/* Фоновое свечение */}
            <div
              className={clsx(
                'pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full blur-3xl opacity-50',
                config.glowClass
              )}
            />

            {/* Фоновый силуэт иконки */}
            <div className="pointer-events-none absolute -bottom-6 -right-6 select-none opacity-20">
              {config.watermarkIcon}
            </div>

            <div
              className="relative z-10 text-base leading-relaxed text-white/90 [&_.ql-container]:!border-0 [&_.ql-editor]:!p-0 [&_.ql-editor]:!text-base [&_.ql-editor]:!text-white/90"
              ref={quillRef}
            />
          </div>
        </div>
      </HeaderInfo>
    </motion.div>
  );
};
